import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  Col,
  Dropdown,
  Icon,
  Menu,
  Modal,
  Row,
  Table,
  Tooltip,
  notification,
} from "antd";

import ButtonStandard from "../../../components/utils/button";
import MenuIcon from "../../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import TagStatus from "../../../components/utils/tagStatus";
import DrawerProductionOrder from "../../../components/drawers/productionOrder";
import { TableActions } from "./styles";
import { numberMask } from "../../../services/helpersMethodsService";
import { resolveProductionOrderError } from "../../../utils/productionOrderErrors";
import {
  cancelProductionOrder,
  deleteProductionOrder,
  indexProductionOrders,
  scheduleProductionOrder,
  showProductionOrder,
} from "../../../services/productionOrderService";

const Column = Table.Column;

const STATUS_COLORS = {
  Calculated: {
    background: "#FFDBBC",
    borderColor: "#FE8D2A",
    color: "#FE8D2A",
  },
  Scheduled: {
    background: "#E4F1FF",
    borderColor: "#4A85AE",
    color: "#4A85AE",
  },
  InProgress: {
    background: "#F6FFC8",
    borderColor: "#A9C133",
    color: "#A9C133",
  },
  ClosedTotal: {
    background: "#C5F1CA",
    borderColor: "#106518",
    color: "#106518",
  },
  ClosedPartial: {
    background: "#C5F1CA",
    borderColor: "#106518",
    color: "#106518",
  },
  Cancelled: {
    background: "#FBC7C7",
    borderColor: "#D44C4C",
    color: "#D44C4C",
  },
};

const ProductionOrderTable = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [orderSelected, setOrderSelected] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        data: { results },
      } = await indexProductionOrders({ groupId, farmId });
      setOrders(results ?? []);
    } catch (error) {
      console.error(error);
      notification.error({
        message: translation.productionOrder.messages.errorLoad,
      });
    } finally {
      setIsLoading(false);
    }
  }, [groupId, farmId, translation]);

  useEffect(() => {
    if (groupId && farmId) {
      fetchData();
    }
  }, [groupId, farmId, fetchData]);

  const openDrawer = useCallback(
    async (id = null, readOnly = false) => {
      if (id == null) {
        setOrderSelected(null);
        setIsReadOnly(false);
        setShowDrawer(true);
        return;
      }

      setIsLoading(true);
      try {
        const {
          data: { results },
        } = await showProductionOrder({ groupId, farmId, id });
        setOrderSelected(results);
        setIsReadOnly(readOnly);
        setShowDrawer(true);
      } catch (error) {
        notification.error({
          message: translation.productionOrder.messages.errorLoad,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [groupId, farmId, translation],
  );

  const closeDrawer = useCallback(() => {
    setShowDrawer(false);
    setOrderSelected(null);
    setIsReadOnly(false);
  }, []);

  const handleSaved = useCallback(() => {
    closeDrawer();
    fetchData();
  }, [closeDrawer, fetchData]);

  const runAction = useCallback(
    async (action, id, successMessage, errorMessage) => {
      setIsLoading(true);
      try {
        await action({ groupId, farmId, id });
        notification.success({ message: successMessage });
        await fetchData();
      } catch (error) {
        notification.error({
          message: resolveProductionOrderError(
            error,
            translation,
            errorMessage,
          ),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [groupId, farmId, fetchData, translation],
  );

  const confirmAction = useCallback(
    (title, onConfirm, okType) => {
      Modal.confirm({
        centered: true,
        title,
        okText: translation.buttons.yes,
        cancelText: translation.buttons.no,
        okType,
        onOk: onConfirm,
        onCancel() {},
      });
    },
    [translation],
  );

  const handleDelete = useCallback(
    (id) =>
      confirmAction(
        translation.productionOrder.messages.confirmDelete,
        () =>
          runAction(
            deleteProductionOrder,
            id,
            translation.productionOrder.messages.successDelete,
            translation.productionOrder.messages.errorDelete,
          ),
        "danger",
      ),
    [confirmAction, runAction, translation],
  );

  const handleSchedule = useCallback(
    (id) =>
      confirmAction(translation.productionOrder.messages.confirmSchedule, () =>
        runAction(
          scheduleProductionOrder,
          id,
          translation.productionOrder.messages.successSchedule,
          translation.productionOrder.messages.errorSchedule,
        ),
      ),
    [confirmAction, runAction, translation],
  );

  const handleCancel = useCallback(
    (id) =>
      confirmAction(translation.productionOrder.messages.confirmCancel, () =>
        runAction(
          cancelProductionOrder,
          id,
          translation.productionOrder.messages.successCancel,
          translation.productionOrder.messages.errorCancel,
        ),
      ),
    [confirmAction, runAction, translation],
  );

  const menu = (record) => (
    <Menu>
      <Menu.Item key="details" onClick={() => openDrawer(record.id, true)}>
        {translation.table.menu.details}
      </Menu.Item>
      {record.status === "Calculated" && (
        <Menu.Item key="edit" onClick={() => openDrawer(record.id, false)}>
          {translation.table.menu.edit}
        </Menu.Item>
      )}
      {record.status === "Calculated" && (
        <Menu.Item key="schedule" onClick={() => handleSchedule(record.id)}>
          {translation.productionOrder.menu.schedule}
        </Menu.Item>
      )}
      {(record.status === "Calculated" || record.status === "Scheduled") && (
        <Menu.Item key="cancel" onClick={() => handleCancel(record.id)}>
          {translation.productionOrder.menu.cancel}
        </Menu.Item>
      )}
      {record.status === "Calculated" && (
        <Menu.Item key="delete" onClick={() => handleDelete(record.id)}>
          {translation.table.menu.delete}
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <>
      <Row type="flex" justify="end" gutter={8} style={{ marginBottom: 16 }}>
        <Col>
          <ButtonStandard
            buttonType="typeWithoutBackground"
            onClick={fetchData}
          >
            <Icon type="reload" />
          </ButtonStandard>
        </Col>
        <Col>
          <ButtonStandard
            type="button"
            buttonType="addNew"
            onClick={() => openDrawer()}
          >
            {translation.productionOrder.buttonNew}
          </ButtonStandard>
        </Col>
      </Row>
      <Table
        loading={isLoading}
        rowKey="id"
        size="small"
        dataSource={orders}
        scroll={{ x: true }}
        style={{ width: "100%", margin: "0" }}
      >
        <Column
          title={translation.productionOrder.table.columns.status}
          dataIndex="status"
          key="status"
          align="left"
          render={(status) => (
            <TagStatus {...(STATUS_COLORS[status] ?? STATUS_COLORS.Cancelled)}>
              {translation.productionOrder.status[status] ?? status}
            </TagStatus>
          )}
        />
        <Column
          title={translation.productionOrder.table.columns.code}
          dataIndex="code"
          key="code"
          align="left"
        />
        <Column
          title={translation.productionOrder.table.columns.supplementName}
          dataIndex="supplementName"
          key="supplementName"
          align="left"
        />
        <Column
          title={translation.productionOrder.table.columns.wagonName}
          dataIndex="wagonName"
          key="wagonName"
          align="left"
        />
        <Column
          title={translation.productionOrder.table.columns.productionDate}
          dataIndex="productionDate"
          key="productionDate"
          align="left"
          render={(productionDate) =>
            moment(productionDate).format("DD/MM/YYYY")
          }
        />
        <Column
          title={translation.productionOrder.table.columns.targetDryMatterKg}
          dataIndex="targetDryMatterKg"
          key="targetDryMatterKg"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={translation.productionOrder.table.columns.plannedDryMatterKg}
          dataIndex="plannedDryMatterKg"
          key="plannedDryMatterKg"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={translation.productionOrder.table.columns.numberOfBatches}
          dataIndex="numberOfBatches"
          key="numberOfBatches"
          align="right"
        />
        <Column
          title={translation.productionOrder.table.columns.totalCost}
          dataIndex="totalCost"
          key="totalCost"
          align="right"
          render={(value) => numberMask(value, true, null, null, 5, 5)}
        />
        <Column
          title={translation.productionOrder.table.columns.costPerKgDryMatter}
          dataIndex="costPerKgDryMatter"
          key="costPerKgDryMatter"
          align="right"
          render={(value) => numberMask(value, true, null, null, 5, 5)}
        />
        <Column
          align="left"
          render={(text, record) => (
            <TableActions>
              {record.status === "Calculated" && (
                <Tooltip title={translation.productionOrder.menu.schedule}>
                  <Icon
                    type="play-circle"
                    className="quickAction"
                    onClick={() => handleSchedule(record.id)}
                  />
                </Tooltip>
              )}
              <Dropdown
                overlay={menu(record)}
                trigger={["click"]}
                key={record.id}
              >
                <MenuContainer>
                  <MenuIcon />
                </MenuContainer>
              </Dropdown>
            </TableActions>
          )}
        />
      </Table>
      <DrawerProductionOrder
        visible={showDrawer}
        order={orderSelected}
        readOnly={isReadOnly}
        onClose={closeDrawer}
        onSave={handleSaved}
      />
    </>
  );
};

export default ProductionOrderTable;
